import {
	buildCurrencyMap,
	convertLocal,
	classifyAccounts,
	groupByTypeAndCurrency,
	computePortfolioSummary,
	computeNativeNetByCurrency,
} from './../../../shared-site-web/net-worth.js';
import { isLiabilityType, isPortfolioType } from './../../../shared/account-types.js';
import { getMockAccounts, getMockCurrencies, getBaseCurrency } from './mock-data/mock-data.js';

/**
 * Report mockups are computed from the locale-specific mock accounts and
 * currencies using the app's real net-worth logic, so the numbers stay
 * consistent across the net worth, wealth breakdown, and portfolio cards.
 */

/**
 * @param {string} lang
 * @returns {{ base: string, cv: (amount: number, from: string, to: string) => number }}
 */
function buildConverter(lang) {
	const base = getBaseCurrency(lang);
	const currencyMap = buildCurrencyMap(
		getMockCurrencies(lang).map((c) => ({ code: c.code, symbol: c.symbol, decimalPlaces: c.decimalPlaces, rate: c.rate })),
	);
	return {
		base,
		cv: (amount, from, to) => convertLocal(amount, from, to, currencyMap),
	};
}

/**
 * @param {string} code
 * @param {string} lang
 * @returns {{ symbol: string, decimalPlaces: number } | null}
 */
export function getCurrencyInfo(code, lang) {
	const c = getMockCurrencies(lang).find((x) => x.code === code);
	return c ? { symbol: c.symbol, decimalPlaces: c.decimalPlaces } : null;
}

/**
 * @typedef {Object} ReportGroupRow
 * @property {string} accountType
 * @property {string} currency
 * @property {number} totalBalance
 * @property {number} totalConverted
 */

/**
 * @param {string} lang
 * @returns {{
 *   assets: Array<import('./mock-data/mock-data.js').MockAccount>,
 *   liabilities: Array<import('./mock-data/mock-data.js').MockAccount>,
 *   assetGroups: Array<ReportGroupRow>,
 *   liabilityGroups: Array<ReportGroupRow>,
 *   assetSum: number,
 *   liabilitySum: number,
 *   netWorth: number,
 *   totalPrincipal: number,
 *   totalGain: number,
 * }}
 */
export function netWorthData(lang) {
	const { base, cv } = buildConverter(lang);
	const { assets, liabilities } = classifyAccounts(getMockAccounts(lang));
	const assetGroups = groupByTypeAndCurrency(assets, cv, base);
	const liabilityGroups = groupByTypeAndCurrency(liabilities, cv, base);
	const assetSum = assets.reduce((s, a) => s + cv(a.balance, a.currency, base), 0);
	const liabilitySum = liabilities.reduce((s, a) => s + cv(a.balance, a.currency, base), 0);
	const { totalPrincipal, totalGain } = computePortfolioSummary(assets, cv, base);
	return {
		assets,
		liabilities,
		assetGroups,
		liabilityGroups,
		assetSum,
		liabilitySum,
		netWorth: assetSum - liabilitySum,
		totalPrincipal,
		totalGain,
	};
}

/**
 * Net wealth by account type. Liabilities contribute negatively so the
 * subtotal always matches net worth.
 * @param {string} lang
 * @returns {Array<{ type: string, net: number }>}
 */
export function wealthByTypeData(lang) {
	const { base, cv } = buildConverter(lang);
	/** @type {Map<string, number>} */
	const totals = new Map();
	for (const a of getMockAccounts(lang)) {
		const sign = isLiabilityType(a.accountType) ? -1 : 1;
		const converted = cv(a.balance, a.currency, base);
		totals.set(a.accountType, (totals.get(a.accountType) || 0) + sign * converted);
	}
	return [...totals.entries()]
		.sort((a, b) => b[1] - a[1])
		.map(([type, net]) => ({ type, net }));
}

/**
 * Net wealth by currency, converted to the base currency. Zero-rate
 * currencies (status miles) stay at zero and never distort the picture.
 * @param {string} lang
 * @returns {Array<{ currency: string, native: number, converted: number }>}
 */
export function wealthByCurrencyData(lang) {
	const { base, cv } = buildConverter(lang);
	const native = computeNativeNetByCurrency(getMockAccounts(lang));
	return Object.entries(native)
		.map(([currency, amount]) => ({ currency, native: amount, converted: cv(amount, currency, base) }))
		.sort((a, b) => b.converted - a.converted);
}

/**
 * @typedef {Object} PortfolioRow
 * @property {import('./mock-data/mock-data.js').MockAccount} account
 * @property {number|null} diff
 * @property {number|null} pct
 */

/**
 * @param {string} lang
 * @returns {Array<PortfolioRow>}
 */
export function portfolioData(lang) {
	return getMockAccounts(lang).filter((a) => isPortfolioType(a.accountType)).map((a) => {
		const diff = a.principal !== null ? a.balance - a.principal : null;
		const pct = a.principal !== null && a.principal !== 0 ? ((a.balance - a.principal) / a.principal) * 100 : null;
		return { account: a, diff, pct };
	});
}
